import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, imageUrl, formatApiErrorDetail, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Upload, X, Star, Video, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const defaultForm = {
  name: "", slug: "", short_description: "", description: "", category_id: "", brand: "",
  mrp: 0, price: 0, stock: 0, unit: "box",
  images: [], primary_image_index: 0, video_url: "",
  is_green_cracker: false, green_cert_number: "",
  peso_license: "", tags: [], is_bestseller: false, is_new: false,
  active: true,
  seo_title: "", seo_description: "", meta_description: "", focus_keyword: "",
  image_alt_text: "", seo_tags: [], og_title: "", og_description: "", seo_faq: [],
  related_product_ids: [], variants: [], multi_packs: [],
};

export default function AdminProductForm() {
  const { productId } = useParams();
  const isEdit = !!productId;
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [relQuery, setRelQuery] = useState("");
  const [relOpen, setRelOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/admin/products").then((r) => setAllProducts(r.data));
    if (isEdit) {
      api.get(`/products/${productId}`)
        .then((r) => setForm({ ...defaultForm, ...r.data }))
        .finally(() => setLoading(false));
    }
  }, [productId, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const relatedProducts = useMemo(() =>
    allProducts.filter((p) => (form.related_product_ids || []).includes(p.id)),
    [allProducts, form.related_product_ids]);

  const relatedSuggestions = useMemo(() => {
    if (!relQuery.trim()) return [];
    const q = relQuery.toLowerCase();
    return allProducts
      .filter((p) => p.id !== productId)
      .filter((p) => !(form.related_product_ids || []).includes(p.id))
      .filter((p) => p.name.toLowerCase().includes(q) || (p.slug || "").includes(q))
      .slice(0, 6);
  }, [relQuery, allProducts, form.related_product_ids, productId]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.name),
        mrp: +form.mrp, price: +form.price, stock: +form.stock,
        primary_image_index: Math.min(Math.max(0, +form.primary_image_index || 0), Math.max(0, form.images.length - 1)),
        variants: (form.variants || []).map((v) => ({ ...v, price: +v.price || 0, mrp: +v.mrp || 0, stock: +v.stock || 0 })),
        multi_packs: (form.multi_packs || []).map((m) => ({ ...m, qty: +m.qty || 1, price: +m.price || 0, mrp: +m.mrp || 0 })),
      };
      if (isEdit) await api.put(`/admin/products/${productId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(`Product ${isEdit ? "updated" : "created"}`);
      navigate("/admin/products");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("images", [...form.images, data.url]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Client-side duration guard (<=15s) — best effort
    if (file.type.startsWith("video/")) {
      const url = URL.createObjectURL(file);
      const check = await new Promise((resolve) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration); };
        v.onerror = () => resolve(0);
        v.src = url;
      });
      if (check > 15.5) {
        toast.error(`Video is ${Math.round(check)}s. Please upload a clip of 15 seconds or less.`);
        e.target.value = "";
        return;
      }
    }
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("video_url", data.url);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setVideoUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => {
    const imgs = form.images.filter((_, j) => j !== i);
    let pi = form.primary_image_index || 0;
    if (i === pi) pi = 0;
    else if (i < pi) pi = Math.max(0, pi - 1);
    setForm((f) => ({ ...f, images: imgs, primary_image_index: pi }));
  };

  const addImageUrl = () => {
    const url = window.prompt("Paste an image URL");
    if (url) set("images", [...form.images, url]);
  };

  // Related
  const addRelated = (p) => {
    set("related_product_ids", [...(form.related_product_ids || []), p.id]);
    setRelQuery("");
    setRelOpen(false);
  };
  const removeRelated = (id) =>
    set("related_product_ids", (form.related_product_ids || []).filter((x) => x !== id));

  // Variants
  const addVariant = () => set("variants", [...(form.variants || []), { name: "", price: form.price, mrp: form.mrp, stock: 0, image: "" }]);
  const updateVariant = (i, patch) => set("variants", form.variants.map((v, j) => j === i ? { ...v, ...patch } : v));
  const removeVariant = (i) => set("variants", form.variants.filter((_, j) => j !== i));

  // Multi-packs
  const addPack = () => set("multi_packs", [...(form.multi_packs || []), { label: "Pack of 2", qty: 2, price: (form.price || 0) * 2, mrp: (form.mrp || 0) * 2 }]);
  const updatePack = (i, patch) => set("multi_packs", form.multi_packs.map((p, j) => j === i ? { ...p, ...patch } : p));
  const removePack = (i) => set("multi_packs", form.multi_packs.filter((_, j) => j !== i));

  if (loading) return <div className="text-hyd-text2">Loading...</div>;

  return (
    <div>
      <Link to="/admin/products" className="text-xs uppercase text-hyd-text2 hover:text-hyd-gold flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Back to products
      </Link>
      <h1 className="font-display text-3xl font-extrabold mt-2">{isEdit ? "Edit product" : "New product"}</h1>

      <form onSubmit={save} className="mt-5 grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-4">
          <Section title="Basic">
            <Grid2>
              <F label="Name">
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={() => !form.slug && set("slug", slugify(form.name))} required />
              </F>
              <F label="Slug">
                <Input value={form.slug} onChange={(e) => set("slug", slugify(e.target.value))} placeholder="auto" />
              </F>
              <F label="Category">
                <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                  <SelectTrigger className="bg-hyd-bg border-hyd-border"><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Brand"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} /></F>
              <F label="Short description (1 line)" full>
                <Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} maxLength={140} placeholder="Shown on cards & tiles" />
              </F>
              <F label="Full description" full>
                <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} />
              </F>
            </Grid2>
          </Section>

          <Section title="Pricing & Stock">
            <Grid2>
              <F label="MRP (₹)"><Input type="number" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} /></F>
              <F label="Selling Price (₹)"><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} required /></F>
              <F label="Stock"><Input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} /></F>
              <F label="Unit"><Input value={form.unit} onChange={(e) => set("unit", e.target.value)} /></F>
            </Grid2>
          </Section>

          <Section title="Media" hint="Star the primary image · Upload one video ≤ 15s for hover preview">
            <div className="grid grid-cols-4 gap-2">
              {form.images.map((im, i) => (
                <div key={i} className={`relative group aspect-square rounded-2xl overflow-hidden border-2 ${i === form.primary_image_index ? "border-hyd-gold" : "border-hyd-border"}`}>
                  <img src={imageUrl(im)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => set("primary_image_index", i)}
                    aria-label="Set as primary"
                    title="Set as primary"
                    className={`absolute top-1 left-1 w-6 h-6 grid place-items-center rounded-full ${i === form.primary_image_index ? "bg-hyd-gold text-hyd-bg" : "bg-hyd-bg/80 text-hyd-text2 hover:text-hyd-gold"}`}
                  >
                    <Star className={`w-3 h-3 ${i === form.primary_image_index ? "fill-hyd-bg" : ""}`} />
                  </button>
                  <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-5 h-5 grid place-items-center bg-hyd-bg/80 rounded-full text-hyd-red">
                    <X className="w-3 h-3" />
                  </button>
                  {i === form.primary_image_index && (
                    <span className="absolute bottom-1 left-1 bg-hyd-gold text-hyd-bg text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase">Primary</span>
                  )}
                </div>
              ))}
              <label className="aspect-square rounded-2xl border-2 border-dashed border-hyd-border grid place-items-center text-xs text-hyd-text2 hover:border-hyd-gold cursor-pointer">
                <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
                <div className="text-center">
                  <Upload className="w-5 h-5 mx-auto text-hyd-gold" />
                  <div className="mt-1">{uploading ? "Uploading..." : "Add photo"}</div>
                </div>
              </label>
            </div>
            <button type="button" onClick={addImageUrl} className="text-xs text-hyd-text2 hover:text-hyd-gold mt-2">+ Add image URL</button>

            {/* Video upload */}
            <div className="mt-4 grid grid-cols-4 gap-2">
              {form.video_url && (
                <div className="relative aspect-square rounded-2xl overflow-hidden border-2 border-hyd-gold col-span-2">
                  <video src={imageUrl(form.video_url)} muted playsInline loop className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("video_url", "")} className="absolute top-1 right-1 w-5 h-5 grid place-items-center bg-hyd-bg/80 rounded-full text-hyd-red">
                    <X className="w-3 h-3" />
                  </button>
                  <span className="absolute bottom-1 left-1 bg-hyd-gold text-hyd-bg text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase">Hover preview</span>
                </div>
              )}
              {!form.video_url && (
                <label className="col-span-2 aspect-video rounded-2xl border-2 border-dashed border-hyd-border grid place-items-center text-xs text-hyd-text2 hover:border-hyd-gold cursor-pointer">
                  <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={uploadVideo} className="hidden" />
                  <div className="text-center">
                    <Video className="w-5 h-5 mx-auto text-hyd-gold" />
                    <div className="mt-1">{videoUploading ? "Uploading video..." : "Add 15-sec video"}</div>
                    <div className="text-[10px] text-hyd-text3 mt-0.5">mp4 / webm · ≤15s · ≤15MB</div>
                  </div>
                </label>
              )}
            </div>
          </Section>

          <Section title="Multi-pack pricing" hint="Sell packs of 2, 3, 5 etc. — customer picks on product page">
            <div className="space-y-2">
              {(form.multi_packs || []).map((mp, i) => (
                <div key={i} className="bg-hyd-bg border border-hyd-border rounded-2xl p-3 grid grid-cols-6 gap-2 items-end">
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Label</Label>
                    <Input value={mp.label} onChange={(e) => updatePack(i, { label: e.target.value })} placeholder="Pack of 2" className="bg-hyd-panel border-hyd-border" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Qty</Label>
                    <Input type="number" value={mp.qty} onChange={(e) => updatePack(i, { qty: e.target.value })} className="bg-hyd-panel border-hyd-border" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">MRP ₹</Label>
                    <Input type="number" value={mp.mrp} onChange={(e) => updatePack(i, { mrp: e.target.value })} className="bg-hyd-panel border-hyd-border" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Price ₹</Label>
                    <Input type="number" value={mp.price} onChange={(e) => updatePack(i, { price: e.target.value })} className="bg-hyd-panel border-hyd-border" />
                  </div>
                  <button type="button" onClick={() => removePack(i)} className="h-9 w-9 grid place-items-center rounded-full text-hyd-red hover:bg-hyd-panel"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <Button type="button" onClick={addPack} variant="secondary" className="bg-hyd-panel2 border border-hyd-border text-hyd-gold rounded-full">
                <Plus className="w-4 h-4 mr-1" /> Add multi-pack option
              </Button>
            </div>
          </Section>

          <Section title="Variants (optional)" hint="Colour, size, flavour, etc.">
            <div className="space-y-2">
              {(form.variants || []).map((v, i) => (
                <div key={i} className="bg-hyd-bg border border-hyd-border rounded-2xl p-3 grid grid-cols-6 gap-2 items-end">
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Name</Label>
                    <Input value={v.name} onChange={(e) => updateVariant(i, { name: e.target.value })} className="bg-hyd-panel border-hyd-border" placeholder="e.g. Red" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">MRP ₹</Label>
                    <Input type="number" value={v.mrp} onChange={(e) => updateVariant(i, { mrp: e.target.value })} className="bg-hyd-panel border-hyd-border" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Price ₹</Label>
                    <Input type="number" value={v.price} onChange={(e) => updateVariant(i, { price: e.target.value })} className="bg-hyd-panel border-hyd-border" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Stock</Label>
                    <Input type="number" value={v.stock} onChange={(e) => updateVariant(i, { stock: e.target.value })} className="bg-hyd-panel border-hyd-border" />
                  </div>
                  <button type="button" onClick={() => removeVariant(i)} className="h-9 w-9 grid place-items-center rounded-full text-hyd-red hover:bg-hyd-panel"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <Button type="button" onClick={addVariant} variant="secondary" className="bg-hyd-panel2 border border-hyd-border text-hyd-gold rounded-full">
                <Plus className="w-4 h-4 mr-1" /> Add variant
              </Button>
            </div>
          </Section>

          <Section title="Related products" hint="Type to search, click to add — shown at the bottom of the product page">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-text3" />
              <Input
                value={relQuery}
                onChange={(e) => { setRelQuery(e.target.value); setRelOpen(true); }}
                onFocus={() => setRelOpen(true)}
                onBlur={() => setTimeout(() => setRelOpen(false), 150)}
                placeholder="Search products by name…"
                className="pl-9 bg-hyd-bg border-hyd-border"
              />
              {relOpen && relatedSuggestions.length > 0 && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-hyd-panel border border-hyd-border rounded-2xl overflow-hidden z-20 shadow-card-dark">
                  {relatedSuggestions.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); addRelated(p); }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-hyd-panel2 text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-hyd-panel2 overflow-hidden shrink-0">
                        {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{p.name}</div>
                        <div className="text-xs text-hyd-text3">{inr(p.price)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-2 space-y-2">
              {relatedProducts.map((p) => (
                <div key={p.id} className="flex items-center gap-2 bg-hyd-bg border border-hyd-border rounded-2xl p-2">
                  <div className="w-8 h-8 rounded-lg bg-hyd-panel2 overflow-hidden shrink-0">
                    {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{p.name}</div>
                    <div className="text-xs text-hyd-text3">{inr(p.price)}</div>
                  </div>
                  <button type="button" onClick={() => removeRelated(p.id)} className="text-hyd-red p-1">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {relatedProducts.length === 0 && <div className="text-xs text-hyd-text3">No related products yet</div>}
            </div>
          </Section>

          <Section title="Compliance">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_green_cracker} onCheckedChange={(v) => set("is_green_cracker", !!v)} />
              Green cracker (PESO/NEERI certified)
            </label>
            <Grid2>
              <F label="Green cert number"><Input value={form.green_cert_number} onChange={(e) => set("green_cert_number", e.target.value)} /></F>
              <F label="PESO license"><Input value={form.peso_license} onChange={(e) => set("peso_license", e.target.value)} /></F>
            </Grid2>
          </Section>

          <Section title="SEO" hint="Controls the browser title, Google snippets, social share cards and Rich-result FAQ schema">
            <Grid2>
              <F label="SEO title" full>
                <Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder="e.g. Buy Golden Sparklers 15cm Online | Hyderabad Crackers" />
                <Hint>{(form.seo_title || "").length} / 60 chars</Hint>
              </F>
              <F label="Meta description" full>
                <Textarea rows={3} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} placeholder="Short search-result snippet (150–160 chars)" />
                <Hint>{(form.meta_description || "").length} / 160 chars</Hint>
              </F>
              <F label="Focus keyword">
                <Input value={form.focus_keyword} onChange={(e) => set("focus_keyword", e.target.value)} placeholder="e.g. golden sparklers" />
              </F>
              <F label="Image ALT text">
                <Input value={form.image_alt_text} onChange={(e) => set("image_alt_text", e.target.value)} placeholder="Describes the primary product image" />
              </F>
              <F label="Keywords / Tags (comma or Enter to add)" full>
                <TagInput value={form.seo_tags || []} onChange={(v) => set("seo_tags", v)} placeholder="Type a tag and press Enter" />
              </F>
              <F label="Open Graph title (social share)" full>
                <Input value={form.og_title} onChange={(e) => set("og_title", e.target.value)} placeholder="Falls back to SEO title" />
              </F>
              <F label="Open Graph description" full>
                <Textarea rows={2} value={form.og_description} onChange={(e) => set("og_description", e.target.value)} placeholder="Shown on WhatsApp / FB / X card" />
              </F>
            </Grid2>

            <div className="mt-2">
              <Label className="text-xs text-hyd-text2 mb-1 block">SEO FAQ (schema.org — Google rich result)</Label>
              <div className="space-y-2">
                {(form.seo_faq || []).map((fq, i) => (
                  <div key={i} className="bg-hyd-bg border border-hyd-border rounded-2xl p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase font-bold text-hyd-gold tracking-widest">Q{i + 1}</span>
                      <Input
                        value={fq.question || ""}
                        onChange={(e) => set("seo_faq", form.seo_faq.map((x, j) => j === i ? { ...x, question: e.target.value } : x))}
                        placeholder="Question"
                        className="bg-hyd-panel border-hyd-border"
                      />
                      <button type="button" onClick={() => set("seo_faq", form.seo_faq.filter((_, j) => j !== i))} className="h-9 w-9 grid place-items-center rounded-full text-hyd-red hover:bg-hyd-panel">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <Textarea
                      rows={2}
                      value={fq.answer || ""}
                      onChange={(e) => set("seo_faq", form.seo_faq.map((x, j) => j === i ? { ...x, answer: e.target.value } : x))}
                      placeholder="Answer"
                      className="bg-hyd-panel border-hyd-border"
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  onClick={() => set("seo_faq", [...(form.seo_faq || []), { question: "", answer: "" }])}
                  variant="secondary"
                  className="bg-hyd-panel2 border border-hyd-border text-hyd-gold rounded-full"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add FAQ
                </Button>
              </div>
            </div>
          </Section>
        </div>

        <aside className="space-y-4 h-fit md:sticky md:top-4">
          <Section title="Visibility">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.active} onCheckedChange={(v) => set("active", !!v)} /> Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_bestseller} onCheckedChange={(v) => set("is_bestseller", !!v)} /> Bestseller
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_new} onCheckedChange={(v) => set("is_new", !!v)} /> New arrival
            </label>
          </Section>
          <Button data-testid={ADMIN.productSave} type="submit" disabled={saving} className="w-full bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold rounded-full h-11">
            {saving ? "Saving..." : isEdit ? "Save changes" : "Create product"}
          </Button>
        </aside>
      </form>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="bg-hyd-panel border border-hyd-border rounded-3xl p-4">
      <div className="mb-3">
        <h3 className="font-display text-lg font-bold">{title}</h3>
        {hint && <p className="text-[11px] text-hyd-text3 mt-0.5">{hint}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Grid2({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function F({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-hyd-text2 mb-1 block">{label}</Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text [&_textarea]:bg-hyd-bg [&_textarea]:border-hyd-border [&_textarea]:text-hyd-text">
        {children}
      </div>
    </div>
  );
}
function Hint({ children }) {
  return <div className="text-[10px] text-hyd-text3 mt-1">{children}</div>;
}
function TagInput({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const parts = draft.split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...value];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    onChange(next);
    setDraft("");
  };
  return (
    <div className="rounded-2xl border border-hyd-border bg-hyd-bg px-2 py-1.5 flex flex-wrap gap-1 min-h-[44px]">
      {value.map((t, i) => (
        <span key={i} className="inline-flex items-center gap-1 bg-hyd-gold/15 text-hyd-gold text-[11px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5">
          {t}
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="hover:text-hyd-red">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
          else if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={add}
        placeholder={value.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[120px] bg-transparent text-sm text-hyd-text placeholder:text-hyd-text3 outline-none px-1"
      />
    </div>
  );
}
